/**
 * Mentor Profile Edit · /mentor/profile
 * --------------------------------------
 * Approved mentors self-curate their public profile here. Updates flow to
 * both mentor_registrations (source of truth) and mentors (public listing).
 *
 * Gated: must be signed in. If not approved yet, shows a pending notice.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Linkedin, User, Briefcase, FileText, Camera, Save, Award,
  Loader2, ExternalLink, ShieldCheck,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NAVY = "#0D1B2A";
const GOLD = "#C8A84B";
const CREAM = "#FBF8EE";
const INK = "#3A4A5C";
const LINE = "#E8DFC9";

export default function MentorProfileEdit() {
  const navigate = useNavigate();
  const { user, token, isAuthenticated, loading: authLoading } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({
    name: "",
    headline: "",
    expertise: "",
    bio: "",
    linkedin_url: "",
    current_role: "",
    company: "",
    phone: "",
    photo_url: "",
  });

  // Load profile
  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate("/login?next=/mentor/profile");
      return;
    }
    axios
      .get(`${API}/mentor/profile`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        const app = r.data?.application || {};
        const pub = r.data?.public_listing || {};
        setProfile(r.data);
        setForm({
          name: pub.name || app.name || user?.full_name || "",
          headline: pub.headline || app.headline || "",
          expertise: pub.expertise || app.expertise || "",
          bio: pub.bio || app.bio || "",
          linkedin_url: pub.linkedin_url || app.linkedin_url || "",
          current_role: pub.current_role || app.current_role || "",
          company: pub.company || app.company || "",
          phone: pub.phone || app.phone || "",
          photo_url: pub.photo_url || app.photo_url || "",
        });
      })
      .catch((err) => {
        if (err?.response?.status === 404) {
          toast.error("No mentor profile yet. Please apply first.");
          setTimeout(() => navigate("/mentor-registration"), 1500);
        } else {
          toast.error("Could not load profile. Please try again.");
        }
      })
      .finally(() => setLoading(false));
  }, [authLoading, isAuthenticated, token, user, navigate]);

  const handlePhoto = async (file) => {
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/jpg", "image/webp"].includes(file.type)) {
      toast.error("PNG, JPG or WebP only.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Photo must be under 5 MB.");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/upload/photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = `${BACKEND_URL}${res.data.url}`;
      setForm((f) => ({ ...f, photo_url: url }));
      toast.success("Photo uploaded · don't forget to Save.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/mentor/profile`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Saved · ${res.data?.updated_fields?.length || 0} fields updated`);
      setProfile(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // Loading state
  if (authLoading || loading) {
    return (
      <div style={{ background: "#EAE3D2", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Georgia, serif", color: NAVY }}>
        <div style={{ textAlign: "center" }}>
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" style={{ color: GOLD }} />
          <div style={{ fontSize: 11, letterSpacing: ".48em", color: GOLD, fontWeight: 700 }}>SKI · AXIS</div>
          <div style={{ fontSize: 14, color: INK, marginTop: 12, fontStyle: "italic" }}>Loading your mentor profile…</div>
        </div>
      </div>
    );
  }

  const isApproved = profile?.is_approved;
  const isPending = profile?.status === "pending";

  return (
    <div style={{ background: "#EAE3D2", minHeight: "100vh", paddingBottom: 80, fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Georgia, serif", color: NAVY }} data-testid="mentor-profile-edit-root">

      {/* Header */}
      <div style={{ background: NAVY, borderBottom: `3px solid ${GOLD}`, padding: "28px 24px", textAlign: "center" }}>
        <div style={{ fontFamily: "Georgia, serif", fontSize: 11, fontWeight: 700, letterSpacing: ".48em", color: GOLD, textTransform: "uppercase" }}>SKI · AXIS · FACULTY</div>
        <div style={{ fontFamily: "Georgia, serif", fontSize: 26, color: "#FAF7F0", marginTop: 10, fontWeight: 700 }}>Your mentor profile</div>
        <div style={{ fontSize: 13, color: "rgba(250,247,240,.7)", marginTop: 6, letterSpacing: ".04em", fontStyle: "italic" }}>
          What appears at <a href="/mentors" style={{ color: GOLD }}>skiaxis.com/mentors</a> — keep it sharp
        </div>
      </div>

      {/* Status banner */}
      <div style={{ maxWidth: 880, margin: "20px auto 0", padding: "0 20px" }}>
        {isPending && (
          <div style={{ background: "#FFF4DA", border: `2px solid ${GOLD}`, padding: "16px 20px", marginBottom: 18, fontSize: 13.5, lineHeight: 1.6 }}>
            <strong style={{ color: "#8A6A14", letterSpacing: ".18em", fontSize: 10.5, textTransform: "uppercase" }}>⏳ Application under review</strong><br />
            You can still edit your profile — when an admin approves you, the latest version will publish straight to <a href="/mentors" style={{ color: "#8A6A14" }}>/mentors</a>.
          </div>
        )}
        {isApproved && (
          <div style={{ background: "#E8F5EC", border: `2px solid #2A7F4F`, padding: "16px 20px", marginBottom: 18, fontSize: 13.5, lineHeight: 1.6, display: "flex", alignItems: "center", gap: 12 }}>
            <ShieldCheck className="w-5 h-5 flex-shrink-0" style={{ color: "#2A7F4F" }} />
            <div>
              <strong style={{ color: "#2A7F4F", letterSpacing: ".18em", fontSize: 10.5, textTransform: "uppercase" }}>Profile published</strong><br />
              Your public listing is live at <a href="/mentors" style={{ color: "#2A7F4F", fontWeight: 700 }}>skiaxis.com/mentors</a>. Edits below take effect the moment you Save.
            </div>
          </div>
        )}
      </div>

      {/* Two-column layout · live preview + edit form */}
      <div style={{ maxWidth: 880, margin: "0 auto", padding: "0 20px", display: "grid", gridTemplateColumns: "1fr 1.6fr", gap: 20 }} className="mp-edit-grid">

        {/* LIVE PREVIEW CARD */}
        <div>
          <div style={{ fontSize: 10.5, color: "#8A6A14", letterSpacing: ".22em", fontWeight: 700, textTransform: "uppercase", marginBottom: 10 }}>Live preview · public card</div>
          <div style={{ background: "#FFFFFF", border: `1px solid ${LINE}`, padding: 22, position: "sticky", top: 20 }} data-testid="profile-preview-card">
            <div style={{ position: "relative", width: 96, height: 96, marginBottom: 16 }}>
              {form.photo_url ? (
                <img src={form.photo_url} alt={form.name} style={{ width: 96, height: 96, borderRadius: "50%", objectFit: "cover", border: `2px solid ${GOLD}` }} />
              ) : (
                <div style={{ width: 96, height: 96, borderRadius: "50%", background: `linear-gradient(135deg, ${GOLD}, #E0C26F)`, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Georgia, serif", fontSize: 30, color: NAVY, fontWeight: 700, border: `2px solid ${GOLD}` }}>
                  {(form.name || "?").split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()}
                </div>
              )}
              {isApproved && (
                <div style={{ position: "absolute", bottom: -4, right: -4, background: GOLD, color: NAVY, padding: "3px 8px", fontSize: 8, fontWeight: 700, letterSpacing: ".18em" }}>VERIFIED</div>
              )}
            </div>
            <div style={{ fontFamily: "Georgia, serif", fontSize: 18, fontWeight: 700, color: NAVY, lineHeight: 1.3 }}>{form.name || "Your name"}</div>
            <div style={{ fontSize: 11.5, color: "#8A6A14", letterSpacing: ".06em", marginTop: 4, fontWeight: 600 }}>{form.expertise || "Your expertise"}</div>
            {(form.current_role || form.company) && (
              <div style={{ fontSize: 12, color: INK, marginTop: 6 }}>
                {[form.current_role, form.company].filter(Boolean).join(" · ")}
              </div>
            )}
            {form.headline && <div style={{ fontSize: 12.5, color: INK, marginTop: 10, fontStyle: "italic", lineHeight: 1.55 }}>"{form.headline}"</div>}
            <div style={{ fontSize: 12.5, color: INK, lineHeight: 1.65, marginTop: 12 }}>{form.bio || <em style={{ color: "#999" }}>Your bio will appear here.</em>}</div>
            {form.linkedin_url && (
              <a href={form.linkedin_url} target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "#8A6A14", marginTop: 12, textDecoration: "none" }}>
                <Linkedin size={14} /> LinkedIn <ExternalLink size={11} />
              </a>
            )}
          </div>
        </div>

        {/* EDIT FORM */}
        <div>
          <div style={{ fontSize: 10.5, color: "#8A6A14", letterSpacing: ".22em", fontWeight: 700, textTransform: "uppercase", marginBottom: 10 }}>Edit your profile</div>
          <div style={{ background: "#FFFFFF", border: `1px solid ${LINE}`, padding: 24 }}>

            {/* Photo upload */}
            <div style={{ marginBottom: 24, padding: 16, background: CREAM, border: `1px solid ${LINE}`, borderLeft: `4px solid ${GOLD}`, display: "flex", alignItems: "center", gap: 16 }}>
              <Camera className="w-5 h-5" style={{ color: GOLD }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: NAVY }}>Profile photo</div>
                <div style={{ fontSize: 11, color: INK, marginTop: 2 }}>PNG · JPG · WebP · under 5 MB</div>
              </div>
              <label htmlFor="mp-photo-upload" data-testid="profile-photo-upload" style={{ background: GOLD, color: NAVY, padding: "10px 16px", fontSize: 11, fontWeight: 700, letterSpacing: ".18em", textTransform: "uppercase", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
                {uploading ? <Loader2 size={13} className="animate-spin" /> : null}
                {uploading ? "Uploading…" : (form.photo_url ? "Replace" : "Upload")}
              </label>
              <input id="mp-photo-upload" type="file" accept="image/png,image/jpeg,image/jpg,image/webp" style={{ display: "none" }} onChange={(e) => handlePhoto(e.target.files?.[0])} />
            </div>

            {/* Fields */}
            <FieldGroup icon={<User size={14} />} label="Full name">
              <input type="text" data-testid="field-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Dr. Neha Dewan" style={inputStyle} />
            </FieldGroup>

            <FieldGroup icon={<Award size={14} />} label="Expertise (one short phrase)" hint="Appears as the gold subtitle on your card · max 80 chars">
              <input type="text" data-testid="field-expertise" value={form.expertise} maxLength={80} onChange={(e) => setForm({ ...form, expertise: e.target.value })} placeholder="e.g. Cross-border venture capital · UAE policy" style={inputStyle} />
            </FieldGroup>

            <FieldGroup icon={<FileText size={14} />} label="Tagline" hint="One-line value prop · italic on your card · max 140 chars">
              <input type="text" data-testid="field-headline" value={form.headline} maxLength={140} onChange={(e) => setForm({ ...form, headline: e.target.value })} placeholder="e.g. I help institutional founders close Series A in the GCC." style={inputStyle} />
            </FieldGroup>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <FieldGroup icon={<Briefcase size={14} />} label="Current role">
                <input type="text" data-testid="field-role" value={form.current_role} onChange={(e) => setForm({ ...form, current_role: e.target.value })} placeholder="e.g. Managing Partner" style={inputStyle} />
              </FieldGroup>
              <FieldGroup label="Company">
                <input type="text" data-testid="field-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="e.g. Acme Capital" style={inputStyle} />
              </FieldGroup>
            </div>

            <FieldGroup label="Bio" hint="3-4 sentences · the public card uses up to 200 chars · the rest appears on your full profile page">
              <textarea data-testid="field-bio" rows={5} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} placeholder="Two or three sentences about who you are, where you've operated, and the kind of founder you back." style={{ ...inputStyle, fontFamily: "inherit", resize: "vertical" }} />
            </FieldGroup>

            <FieldGroup icon={<Linkedin size={14} />} label="LinkedIn URL">
              <input type="url" data-testid="field-linkedin" value={form.linkedin_url} onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })} placeholder="https://linkedin.com/in/..." style={inputStyle} />
            </FieldGroup>

            <FieldGroup label="Phone (private · admin contact only)">
              <input type="tel" data-testid="field-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+971 50 ..." style={inputStyle} />
            </FieldGroup>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 24, paddingTop: 20, borderTop: `1px solid ${LINE}` }}>
              <button type="button" onClick={() => navigate("/mentor-workshop-preview")} style={{ background: "transparent", border: `1.5px solid ${NAVY}`, color: NAVY, padding: "12px 20px", fontSize: 11, fontWeight: 700, letterSpacing: ".18em", textTransform: "uppercase", cursor: "pointer" }} data-testid="back-to-dashboard">
                ← Back to dashboard
              </button>
              <button type="button" onClick={handleSave} disabled={saving} data-testid="save-profile-btn" style={{ background: GOLD, color: NAVY, border: "none", padding: "14px 28px", fontSize: 12, fontWeight: 700, letterSpacing: ".20em", textTransform: "uppercase", cursor: saving ? "wait" : "pointer", display: "inline-flex", alignItems: "center", gap: 8, opacity: saving ? 0.7 : 1 }}>
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                {saving ? "Saving…" : "Save profile"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 760px) {
          .mp-edit-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}

const FieldGroup = ({ icon, label, hint, children }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, letterSpacing: ".14em", color: "#8A6A14", textTransform: "uppercase", marginBottom: 6 }}>
      {icon} {label}
    </label>
    {children}
    {hint && <div style={{ fontSize: 11, color: INK, marginTop: 4, fontStyle: "italic", lineHeight: 1.5 }}>{hint}</div>}
  </div>
);

const inputStyle = {
  width: "100%",
  padding: "10px 14px",
  fontSize: 13.5,
  border: `1px solid ${LINE}`,
  background: "#FFFFFF",
  color: NAVY,
  outline: "none",
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};
